import { hasUIDField, UIDContext } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import type { CustomType } from "@prismicio/types-internal/lib/customtypes"
import { useCallback, useContext, useMemo, useState } from "react"

import { getReleaseId } from "../app/services/release"
import { type DocumentData, type NewDocumentData } from "./documentData"
import { setGeneratedUID } from "./extractRelevantTextFromContent"

export function useDraft(data: DocumentData): DocumentDraft {
  const { type, customType } = data

  const { meta, version } =
    data.type === "existing" ? data : { meta: undefined, version: undefined }

  const document = getDocument()

  const metaId = meta?.id

  const [previousMetaId, setPreviousMetaId] = useState(metaId)
  const [nextAnimation, setNextAnimation] = useState<NextAnimation>()
  const [cancelSignal, setCancelSignal] = useState(0)

  function getDocument() {
    if (data.type === "existing") return data.document
    if (isNewDocFromCopy(data)) return data.asCopy.document
    return {}
  }

  function getDocData() {
    if (isNewDocFromCopy(data)) return data.asCopy.document
    if (data.type === "existing" && data.initialDraft !== undefined)
      return data.initialDraft

    return document
  }

  function getEditedAt() {
    if (isNewDocFromCopy(data)) return new Date()

    if (data.type === "existing" && data.initialDraft !== undefined)
      return new Date()

    return undefined
  }

  function getDocumentTags() {
    if (isNewDocFromCopy(data)) return new Set(data.asCopy.tags)

    return new Set(version?.tags ?? [])
  }

  const [draft, setDraft] = useState<Draft>({
    id: crypto.randomUUID(),
    document: getDocData(),
    documentTags: getDocumentTags(),
    editedAt: getEditedAt()
  })

  const { editedUID } = useContext(UIDContext)

  const isFromCopy = isNewDocFromCopy(data)

  const edit = useCallback(
    (document: DocumentT) =>
      setDraft((prevDraft) => ({
        ...prevDraft,
        editedAt: new Date(),
        document: transformDocumentAfterEdit({
          document,
          customType,
          type,
          editedUID: editedUID.current,
          isFromCopy
        })
      })),
    [type, customType, editedUID, isFromCopy]
  )

  const resetDraft = useCallback(
    (animate = false) =>
      setDraft((draft) => ({
        id: animate ? crypto.randomUUID() : draft.id,
        document,
        documentTags: new Set(version?.tags ?? [])
      })),
    [document, version]
  )

  const animateNextChange = useCallback((toVersionId?: string) => {
    setNextAnimation({ toVersionId })
  }, [])

  const cancel = useCallback(() => {
    setCancelSignal((signal) => signal + 1)
  }, [])

  const metaIdChanged = metaId !== previousMetaId
  if (metaIdChanged) setPreviousMetaId(metaId)

  const releaseId = version ? getReleaseId(version) : undefined

  // Batch and only reset a single time.
  const resetDraftSignal = useMemo(
    () => [cancelSignal, version?.status, version?.version_id, releaseId],
    [cancelSignal, releaseId, version?.status, version?.version_id]
  )

  useOnChange(resetDraftSignal, () => {
    // When the meta.id changes, the version_id always changes as well and
    // it's a big enough change that we want to force the animation.
    // Typically this happen when navigating with the previous button, etc.
    resetDraft(Boolean(nextAnimation) || metaIdChanged)
    setNextAnimation(undefined)
  })

  const updateTags = useCallback(
    (newTags: Set<string>) =>
      setDraft((prevDraft) => ({
        ...prevDraft,
        editedAt: new Date(),
        documentTags: newTags
      })),
    []
  )

  return useMemo(
    () => ({
      ...draft,
      edit,
      cancel,
      nextAnimation,
      animateNextChange,
      updateTags
    }),
    [draft, edit, cancel, nextAnimation, updateTags, animateNextChange]
  )
}

export interface DocumentDraft {
  // The draft id is used to keep a stable reference after each draft reset.
  // This helps run animations when a significant change occured.
  id: string
  document: DocumentT
  editedAt?: Date
  edit: (newDocument: DocumentT) => void
  documentTags: Set<string>
  nextAnimation: NextAnimation
  updateTags: (newTags: Set<string>) => void
  cancel: () => void
  animateNextChange: (toVersionId?: string) => void
}

type NextAnimation = { toVersionId?: string } | undefined

export interface Draft {
  id: string
  editedAt?: Date
  document: DocumentT
  documentTags: Set<string>
}

type NewDocFromCopy = NewDocumentData & {
  asCopy: NonNullable<NewDocumentData["asCopy"]>
}

function isNewDocFromCopy(doc: DocumentData): doc is NewDocFromCopy {
  return doc.type === "new" && Boolean(doc.asCopy)
}

interface TransformDocumentAfterEditParams {
  type: "new" | "existing"
  document: DocumentT
  customType: CustomType
  editedUID: boolean
  isFromCopy: boolean
}

function transformDocumentAfterEdit(params: TransformDocumentAfterEditParams) {
  const { type, document, customType, editedUID, isFromCopy } = params

  const shouldGenerateUID =
    type === "new" && hasUIDField(customType) && !editedUID && !isFromCopy

  if (!shouldGenerateUID) return document

  return setGeneratedUID(document)
}
